import React, { useState } from 'react';
import parse from 'html-react-parser';
import { formConfig } from '@Utils/formConfig';
import { useGlobalState } from '@Store/auth.store';
import Modal from '../Modal';
import ButtonContainer from '../ButtonContainer';
import { postHttp } from '@Utils/useHttp';
import LoadingSpinner from '../LoadingSpinner';
import { TGraphicAlt } from '../../utils/types';
import Graphics from '../Graphics';
import RecommendationTextCard from '../RecommendationTextCard';

interface RecommendationModalProps {
	modalIcon?: string
	modalHeader?: string
	modalOverskrift: string
	modalText: string
	btn_link1?: string
	btn_text1?: string
	btn_link2?: string
	btn_text2?: string
	modalFooter?: string
	graphic?: TGraphicAlt
	anbefalingsBaggrund?: string
	visAnbefalingSamtykke?: boolean
	visRoedBaggrund?: boolean
	samtykke?: boolean
	samtykkeLabel?: string
	samtykkeTekst?: string
	telefon?: string
	email?: string
	landlinephone?: string
	blockId?: string
	onClose: () => void
}

const RecommendationModal = (props: RecommendationModalProps) => {
	const {
		modalIcon,
		modalHeader,
		modalOverskrift,
		modalText,
		btn_link1,
		btn_text1,
		btn_link2,
		btn_text2,
		modalFooter,
		graphic,
		anbefalingsBaggrund,
		visAnbefalingSamtykke,
		samtykke,
		samtykkeLabel,
		samtykkeTekst,
		telefon,
		email,
		landlinephone,
		blockId,
		onClose
	} = props;

	const [showConsent, setShowConsent] = useState(false);
	const [showReceipt, setShowReceipt] = useState(false);
	const [errorEmail, setErrorEmail] = useState(false);
	const [errorPhn, setErrorPhn] = useState(false);
	const [fields, setFields] = useState({ telefon, email, samtykke, blockId, landlinephone });
	const [isLoading, setIsLoading] = useState(false);
	const [loggedInType] = useGlobalState('loggedInType');

	const onSubmit = async (e) => {
		e.preventDefault();

		if (isFormValid()) {
			if (loggedInType.toLowerCase() === 'mitid') {
				setIsLoading(true);

				const response = await postHttp('/Profile/GemKontaktInfo', { ...fields, kontaktOplysningKilde: 'Selvbetjening' });

				if (response?.status) {
					setIsLoading(false);
					setShowReceipt(true);
				}
			}
		}
	};

	const handleConsent = () => {
		setShowConsent(true);
	};

	const isFormValid = () => {
		let isValid = true;

		if (!fields.telefon.match(formConfig.telephone)) {
			setErrorPhn(true);
			isValid = false;
		}

		if (!fields.email.match(formConfig.emailDomain)) {
			setErrorEmail(true);
			isValid = false;
		}

		return isValid;
	};

	return (
		<Modal
			onClose={ onClose }
			width={ (showConsent || graphic?.url || anbefalingsBaggrund) ? 'large' : 'small' }
			fixedHeight={ true }
			customStyle={ { paddingBottom: '2.5rem' } }
		>
			{ !showConsent && (
				<div className="modal-content-container">
					<div>
						{ modalIcon && <img src={ modalIcon } alt="icon" className="modal-icon" /> }
						<div className="modal-content">
							<div className="modal-content__header">{ modalHeader && parse(modalHeader) }</div>
							<h1 className="modal-content__title">{ parse(modalOverskrift) }</h1>
							<div className="modal-content__body">{ parse(modalText) }</div>
						</div>
						<ButtonContainer flex wrap>
							{ btn_text1 && !visAnbefalingSamtykke &&
								<a href={ btn_link1 } className="btn" target="_blank" rel="noreferrer">{ btn_text1 }</a> }
							{ btn_text2 && btn_text1 && !visAnbefalingSamtykke &&
								<a href={ btn_link2 } className="btn btn-secondary" target="_blank" rel="noreferrer">{ btn_text2 }</a> }
							{ btn_text2 && !visAnbefalingSamtykke && !btn_text1 &&
								<button className="btn" onClick={ onClose }>{ btn_text2 }</button> }
							{ visAnbefalingSamtykke && btn_text1 && btn_text2 && (
								<>
									<button className="btn" onClick={ handleConsent }>{ btn_text1 }</button>
									<button className="btn btn-secondary" onClick={ onClose }>{ btn_text2 }</button>
								</>
							) }
						</ButtonContainer>
						{ modalFooter && (
							<Modal.Footer customStyle={ { paddingTop: '1rem' } }>
								{ parse(modalFooter) }
							</Modal.Footer>
						) }
					</div>
					{ !anbefalingsBaggrund && graphic && graphic?.url && (
						<div className="modal-graphics">
							<Graphics data={ { Type: graphic.type, Url: graphic.url } } />
						</div>
					) }
					{ anbefalingsBaggrund && (
						<div className="modal-graphics">
							<RecommendationTextCard body={ anbefalingsBaggrund } />
						</div>
					) }

				</div>
			) }
			{ showConsent && (
				<div className="consent-container">
					<div className="modal-content">
						{ parse(samtykkeTekst || '') }
					</div>
					{ showReceipt && (
						<div className="consent-receipt">
							<svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="13" cy="13" r="13" fill="#92004D" />
								<path d="M8 12.875L11.9552 17L18.875 8" stroke="white" strokeWidth="2" />
							</svg>
							<div>
								<p><strong>Du har nu afgivet samtykke.</strong></p>
								<p>Du kan altid se og rette det inde på din profil.</p>
							</div>
						</div>
					) }
					{ !showReceipt && (
						<form onSubmit={ onSubmit } className="lhp-form">
							<p><strong>Dine oplysninger</strong></p>
							<fieldset >
								<label htmlFor="email"><span className="label-text">Din mail</span>
									<input
										type="text"
										name="email"
										className={ errorEmail ? 'input-text error' : 'input-text' }
										value={ fields.email }
										onChange={ (e) => {
											setFields({ ...fields, email: e.target.value }); setErrorEmail(false);
										} }
									/>
								</label>
								<label htmlFor="phn"><span className="label-text">Dit mobilnummer</span>
									<input
										type="tel"
										name="phn"
										className={ errorPhn ? 'input-tel error' : 'input-tel' }
										defaultValue={ fields.telefon }
										onChange={ (e) => {
											setFields({ ...fields, telefon: e.target.value }); setErrorPhn(false);
										} }
									/>
								</label>

								<div className="consent-switch">
									<label
										htmlFor="switch"
										className="switch"
										onClick={ () => {
											setFields({ ...fields, samtykke: !fields.samtykke });
										} }>
										<input
											type="checkbox"
											name="switch"
											onChange={ () => {
												setFields({ ...fields, samtykke: !fields.samtykke });
											} }
											checked={ !!fields.samtykke } />
										<span className="toggle-switch" />
									</label>
									<p className="consent-toggle">{ samtykkeLabel }</p>
								</div>
							</fieldset>
							<ButtonContainer
								m_medium
							>
								<button className="btn btn-primary" type="submit" disabled={ !fields.samtykke }>Gem</button>
							</ButtonContainer>
							{ isLoading &&
								<LoadingSpinner /> }
						</form>
					) }
				</div>
			) }
		</Modal>
	);
};

export default RecommendationModal;